import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Button, Logo, WaitingSpinner } from '@shared/widgets';
import { useGetImageStore, useGetSurveyStore, usePostSurveyStore } from '@lib/store';
import { Question } from './widgets';
import { PostSurveyRequest, QuestionsType } from '@shared/api/rest/survey';
import { Confirmation } from '@widgets/confirmation';
import { CategoryTypeEnum } from '@shared/api/models';

interface DefaultValuesType {
  [key: string]: QuestionsType[] | null;
}

export const Survey = () => {
  const getSurveyStore = useGetSurveyStore();
  const postSurveyStore = usePostSurveyStore();
  const imageStore = useGetImageStore();
  const { surveyId, clientId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const getDefaultValues = (questions?: QuestionsType[]): DefaultValuesType => {
    if (!questions) return {};
    return questions.reduce<DefaultValuesType>((acc, question) => {
      acc[question.Id] = question.MultipleAnswers ? [] : null;
      return acc;
    }, {});
  };

  const formControl = useForm({
    mode: 'onChange',
    defaultValues: getDefaultValues(getSurveyStore.data?.Questions),
  });

  useEffect(() => {
    if (surveyId && clientId) {
      getSurveyStore.get(surveyId, clientId);
      imageStore.get(surveyId, clientId);
    }
  }, [surveyId, clientId]);

  useEffect(() => {
    if (getSurveyStore.data?.Questions) {
      formControl.reset(getDefaultValues(getSurveyStore.data.Questions));
    }
  }, [getSurveyStore.data]);

  const onSubmit = async (data: DefaultValuesType) => {
    if (isSubmitting || postSurveyStore.success) return;

    setIsSubmitting(true);
    postSurveyStore.clear();

    const formattedData = Object.entries(data)
      .filter(([_, answerId]) => answerId !== null)
      .map(([questionId, answerId]) => ({
        QuestionId: questionId,
        Answer: answerId,
      }));

    try {
      await postSurveyStore.create({ Answers: formattedData } as unknown as PostSurveyRequest, surveyId!, clientId!);
      await getSurveyStore.get(surveyId!, clientId!);
    } catch (error) {
      console.log(`Error: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Logo />
      <div className='flex flex-col text-center items-center bg-white text-black text-[23px] font-light min-h-[140px] relative shadow-[0_4px_4px_rgba(87,100,126,0.21)] rounded-[3px] max-w-[752px] mx-auto'>
        {getSurveyStore.loading && !postSurveyStore.success && (
          <div className='h-full absolute top-0 left-0 w-full'>
            <WaitingSpinner active />
          </div>
        )}
        {!getSurveyStore.loading && getSurveyStore.error && (
          <div className='bodonyFont text-[#d00000] text-[28px] my-auto'>The survey is not found</div>
        )}
        {!getSurveyStore.loading && getSurveyStore.data?.Questions && !getSurveyStore.error ? (
          <>
            <form className='w-full' onSubmit={formControl.handleSubmit(onSubmit)}>
              <div className='px-8'>
                <div className='text-inherit text-2xl tracking-wide bodonyFont border-b border-b-gray-400 p-[14px] mt-[1em] mb-[0.75em]'>
                  Your Voice, Our Inspiration!
                </div>

                {getSurveyStore.data.Questions.sort((a, b) => a.Order - b.Order).map((question) => (
                  <Controller
                    key={question.Id}
                    name={question.Id}
                    control={formControl.control}
                    rules={{
                      required: question.IsMandatory && question.CategoryType !== CategoryTypeEnum.DefaultText ? 'Please pick the answer' : false,
                    }}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Question className='mb-10' question={question} value={value} onChange={onChange} error={error} />
                    )}
                  />
                ))}
                {!imageStore.error && (
                  <div className='my-5 md:my-10 w-full max-w-[620px] h-auto aspect-video mx-auto relative'>
                    {imageStore.loading ? (
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <WaitingSpinner active />
                      </div>
                    ) : imageStore.data ? (
                      <img src={imageStore.data} alt='Survey attachment' className='w-full h-full object-contain' />
                    ) : null}
                  </div>
                )}
              </div>
              <div className='flex items-center justify-center mb-10 relative border-t-2 border-gray-400 pt-8 flex-col'>
                {postSurveyStore.loading || isSubmitting ? (
                  <div className=' min-w-[180px] h-12 '>
                    <WaitingSpinner active />
                  </div>
                ) : (
                  <Button
                    className='text-white bg-[#ffc42c] min-w-[180px] text-base h-12 rounded-[6px]'
                    caption='Submit'
                    type='submit'
                    disabled={isSubmitting || postSurveyStore.success!}
                  />
                )}
                {postSurveyStore.error && (
                  <div className='text-[#d00000] text-sm mt-2.5 max-w-[744px] w-full'>Something went wrong. Try again later.</div>
                )}
              </div>
            </form>
          </>
        ) : (
          (postSurveyStore.success || getSurveyStore.success) && <Confirmation />
        )}
      </div>
    </>
  );
};
